import { useEffect, useState } from "react";
import { Crown, Search, Save, Loader2, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import apiClient, { getErrorMessage } from "@/lib/apiClient";
import { AdminHeader, TableWrap, Th, Td, Tr, StatusChip, Spinner, EmptyRow, Field } from "@/components/admin/ui";

const TIER_STYLE = {
  basic: { color: "#94a3b8", label: "Basic" },
  pro: { color: "#00e5ff", label: "Pro" },
  champ: { color: "#00ff9d", label: "Champion" },
  vip: { color: "#ff2e97", label: "VIP" },
};

const hoursFromCredits = (n) => {
  const h = Number(n || 0) * 0.5;
  return Number.isInteger(h) ? h : h.toFixed(1);
};

export default function AdminMemberships() {
  const [plans, setPlans] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [replenishing, setReplenishing] = useState(false);
  const [q, setQ] = useState("");

  const load = () => {
    Promise.all([apiClient.get("/admin/membership-plans"), apiClient.get("/admin/subscriptions")])
      .then(([p, s]) => { setPlans(p.data); setSubs(s.data); })
      .catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const setPlanField = (idx, key, value) => {
    setPlans((prev) => prev.map((p, i) => i === idx ? { ...p, [key]: value } : p));
  };

  const setPopular = (type) => {
    setPlans((prev) => prev.map((p) => ({ ...p, most_popular: p.type === type })));
  };

  const savePlans = async () => {
    setSaving(true);
    try {
      const payload = {
        plans: plans.map((p) => ({
          type: p.type,
          name: p.name,
          price: Number(p.price),
          discount_percentage: Number(p.discount_percentage),
          points_multiplier: Number(p.points_multiplier),
          monthly_credits: Number(p.monthly_credits),
          monthly_tournament_entries: Number(p.monthly_tournament_entries),
          most_popular: !!p.most_popular,
        })),
      };
      const { data } = await apiClient.put("/admin/membership-plans", payload);
      setPlans(data);
      toast.success("Membership tiers updated");
    } catch (err) { toast.error(getErrorMessage(err, "Save failed")); } finally { setSaving(false); }
  };

  const runReplenish = async () => {
    setReplenishing(true);
    try {
      const { data } = await apiClient.post("/admin/memberships/replenish");
      toast.success(`${data.month}: ${data.applied} member(s) received this month's perks, ${data.skipped} skipped`);
    } catch (err) { toast.error(getErrorMessage(err, "Replenish failed")); } finally { setReplenishing(false); }
  };

  const filtered = subs.filter((s) => {
    const t = q.toLowerCase();
    return !t || `${s.user_name || ""} ${s.user_email || ""} ${s.plan_name || ""}`.toLowerCase().includes(t);
  });

  if (loading) return <Spinner label="Loading memberships" />;

  const activeCount = subs.filter((s) => s.status === "active").length;

  return (
    <div>
      <AdminHeader
        title="Memberships"
        subtitle={`${activeCount} active subscriptions`}
        right={
          <button onClick={runReplenish} disabled={replenishing} className="neon-outline-btn px-5 py-2.5" data-testid="replenish-memberships">
            {replenishing ? <><Loader2 className="w-4 h-4 animate-spin" /> Running...</> : <><RefreshCw className="w-4 h-4" /> Replenish this month</>}
          </button>
        }
      />

      <div className="mb-6 p-4 rounded-2xl" style={{ background: "rgba(139,92,246,0.06)", border: "1px solid rgba(139,92,246,0.25)" }}>
        <p className="text-slate-300 text-sm">
          Adjust the perks for each membership tier. Numbers here <span className="text-white font-semibold">apply automatically</span> at every booking (discount, points multiplier) and are <span className="text-white font-semibold">granted monthly</span> to active subscribers (booking credits, tournament entries).
        </p>
      </div>

      <h3 className="font-display text-lg font-bold text-white uppercase tracking-wide mb-4">Tier Configuration</h3>
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
        {plans.map((p, i) => {
          const st = TIER_STYLE[p.type] || TIER_STYLE.basic;
          const hrs = hoursFromCredits(p.monthly_credits);
          return (
            <div key={p.type} className="glass rounded-2xl p-5" data-testid={`plan-editor-${p.type}`}
              style={{ borderColor: `${st.color}55`, borderWidth: "1px", borderStyle: "solid" }}>
              <div className="flex items-center gap-2 mb-3">
                <Crown className="w-5 h-5 tier-crown" style={{ "--tier-ic": st.color }} />
                <p className="font-display font-bold text-white uppercase text-sm tracking-wider" style={{ color: st.color }}>{st.label}</p>
              </div>
              <button
                type="button"
                onClick={() => setPopular(p.type)}
                data-testid={`plan-popular-toggle-${p.type}`}
                className="w-full mb-3 flex items-center justify-center gap-2 px-3 py-2 rounded-xl font-semibold text-xs uppercase tracking-wide transition-all"
                style={p.most_popular
                  ? { background: `${st.color}22`, border: `1px solid ${st.color}88`, color: st.color }
                  : { background: "rgba(8,11,22,0.5)", border: "1px solid rgba(120,150,220,0.18)", color: "#94a3b8" }}>
                <Sparkles className="w-3.5 h-3.5" /> {p.most_popular ? "Most Popular" : "Mark Most Popular"}
              </button>
              <Field label="Display name">
                <input className="input-cyber" value={p.name || ""} onChange={(e) => setPlanField(i, "name", e.target.value)} data-testid={`plan-name-${p.type}`} />
              </Field>
              <Field label="Monthly price ($)">
                <input type="number" min="0" step="0.01" className="input-cyber" value={p.price} onChange={(e) => setPlanField(i, "price", e.target.value)} data-testid={`plan-price-${p.type}`} />
              </Field>
              <Field label="Discount % on bookings">
                <input type="number" min="0" max="100" className="input-cyber" value={p.discount_percentage} onChange={(e) => setPlanField(i, "discount_percentage", e.target.value)} data-testid={`plan-discount-${p.type}`} />
              </Field>
              <Field label="Points multiplier">
                <input type="number" min="1" step="0.1" className="input-cyber" value={p.points_multiplier} onChange={(e) => setPlanField(i, "points_multiplier", e.target.value)} data-testid={`plan-mult-${p.type}`} />
              </Field>
              <Field label={`Monthly booking credits (30 min each) — ${hrs} free hrs/mo`}>
                <input type="number" min="0" className="input-cyber" value={p.monthly_credits} onChange={(e) => setPlanField(i, "monthly_credits", e.target.value)} data-testid={`plan-credits-${p.type}`} />
              </Field>
              <Field label="Free tournament entries / month">
                <input type="number" min="0" className="input-cyber" value={p.monthly_tournament_entries} onChange={(e) => setPlanField(i, "monthly_tournament_entries", e.target.value)} data-testid={`plan-entries-${p.type}`} />
              </Field>
            </div>
          );
        })}
      </div>
      <button onClick={savePlans} disabled={saving} className="neon-btn px-6 py-2.5 mb-10" data-testid="plans-save">
        {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : <><Save className="w-4 h-4" /> Save All Tiers</>}
      </button>

      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-lg font-bold text-white uppercase tracking-wide">Subscribers</h3>
        <div className="relative">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input className="input-cyber pl-10 w-64" placeholder="Search subscriber" value={q}
            onChange={(e) => setQ(e.target.value)} data-testid="sub-search" />
        </div>
      </div>
      <TableWrap>
        <thead><Tr><Th>User</Th><Th>Plan</Th><Th>Status</Th><Th>Start</Th><Th>End</Th><Th>Perks month</Th></Tr></thead>
        <tbody>
          {filtered.length === 0 ? <EmptyRow colSpan={6} text="No subscriptions found" /> : filtered.map((s) => (
            <Tr key={s.id}>
              <Td>
                <p className="text-white font-semibold" data-testid={`sub-user-${s.id}`}>{s.user_name || "—"}</p>
                <p className="text-slate-500 text-xs">{s.user_email}</p>
              </Td>
              <Td>{s.plan_name}</Td>
              <Td><StatusChip status={s.status} /></Td>
              <Td className="text-slate-400">{s.start_date ? new Date(s.start_date).toLocaleDateString() : "—"}</Td>
              <Td className="text-slate-400">{s.end_date ? new Date(s.end_date).toLocaleDateString() : "—"}</Td>
              <Td className="text-slate-400">{s.last_benefits_month || "—"}</Td>
            </Tr>
          ))}
        </tbody>
      </TableWrap>
    </div>
  );
}
